import React, { useState } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Trash2, Edit, Save, X, GripVertical } from 'lucide-react';

function SortableTemplateItem({
    id,
    item,
    itemType,
    editingId,
    editedItem,
    onStartEditing,
    onCancelEditing,
    onSaveEditing,
    onEditChange,
    onDelete,
    type
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg border ${
                isDragging ? 'shadow-lg' : ''
            }`}
        >
            <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
            >
                <GripVertical size={16} />
            </div>

            <div className="flex-1 grid grid-cols-3 gap-4 items-center">
                {editingId === item.id ? (
                    <>
                        <div>
                            <input
                                type="text"
                                name={itemType === 'Task' ? 'task' : 'label'}
                                value={editedItem.task ?? editedItem.label}
                                onChange={onEditChange}
                                className="w-full p-1 border rounded text-sm"
                            />
                        </div>
                        <div>
                            {itemType === 'Task' ? (
                                <input
                                    type="text"
                                    name="category"
                                    value={editedItem.category}
                                    onChange={onEditChange}
                                    className="w-full p-1 border rounded text-sm"
                                />
                            ) : (
                                <span className="text-sm text-gray-500 font-mono">{item.key}</span>
                            )}
                        </div>
                        <div className="flex justify-end items-center space-x-1">
                            <button
                                onClick={() => onSaveEditing(type)}
                                className="p-2 text-green-600"
                            >
                                <Save size={16} />
                            </button>
                            <button
                                onClick={onCancelEditing}
                                className="p-2 text-gray-500"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="text-sm">{item.task || item.label}</p>
                        <p className="text-sm text-gray-500">{item.category || item.key}</p>
                        <div className="flex justify-end items-center space-x-1">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onStartEditing(item);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                className="p-2 text-gray-500"
                            >
                                <Edit size={16} />
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(type, item.id);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                className="p-2 text-gray-500"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

const SortableMasterTemplateSection = ({
    type,
    title,
    itemType,
    items,
    editingId,
    editedItem,
    onStartEditing,
    onCancelEditing,
    onSaveEditing,
    onEditChange,
    onDelete,
    onReorder,
    onOpenAddModal
}) => {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            // Only activate drag after 8px of movement - prevents clicks from being captured
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Sort items by display_order if available, otherwise keep original order
    const sortedItems = [...(items || [])].sort((a, b) => {
        return (a.display_order || 0) - (b.display_order || 0);
    });

    function handleDragEnd(event) {
        const { active, over } = event;

        if (active.id !== over.id && onReorder) {
            const oldIndex = sortedItems.findIndex(item => item.id === active.id);
            const newIndex = sortedItems.findIndex(item => item.id === over.id);
            const newOrder = arrayMove(sortedItems, oldIndex, newIndex);
            const orderedIds = newOrder.map(item => item.id);
            onReorder(type, orderedIds);
        }
    }

    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border">
            <h3 className="text-xl font-bold capitalize">{type} {title}</h3>
            <div className="mt-4 space-y-2">
                <div className="grid grid-cols-3 gap-4 text-xs font-bold text-gray-500 uppercase p-2 ml-7">
                    <span>{itemType === 'Task' ? 'Task' : 'Label'}</span>
                    <span>{itemType === 'Task' ? 'Category' : 'Key'}</span>
                    <span className="text-right">Actions</span>
                </div>

                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    onDragStart={(e) => {
                        // Only prevent default during actual drag, not on click
                        if (e.active) {
                            // Drag is starting
                        }
                    }}
                    // Prevent DndContext from creating a global overlay
                    autoScroll={false}
                    // Scope the DndContext to only this container
                    onDragCancel={() => {
                        // Ensure drag cancellation doesn't leave any overlays
                    }}
                >
                    <SortableContext
                        items={sortedItems.map(item => item.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <div 
                            className="space-y-1" 
                            style={{ 
                                position: 'relative', 
                                zIndex: 1,
                                // Ensure this container doesn't block clicks outside
                                isolation: 'isolate'
                            }}
                            onClick={(e) => {
                                // Only stop propagation for clicks within sortable items
                                // Allow clicks on buttons and other interactive elements to work normally
                                if (e.target.closest('button') || e.target.closest('a')) {
                                    e.stopPropagation();
                                }
                            }}
                        >
                            {sortedItems.map(item => (
                                <SortableTemplateItem
                                    key={item.id}
                                    id={item.id}
                                    item={item}
                                    itemType={itemType}
                                    editingId={editingId}
                                    editedItem={editedItem}
                                    onStartEditing={onStartEditing}
                                    onCancelEditing={onCancelEditing}
                                    onSaveEditing={onSaveEditing}
                                    onEditChange={onEditChange}
                                    onDelete={onDelete}
                                    type={type}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            </div>

            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenAddModal(type);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2"
            >
                <Plus size={18} /> Add {type} {itemType}
            </button>
        </div>
    );
};

export default SortableMasterTemplateSection;